import { Injectable, inject, Injector } from '@angular/core';
import { doc, writeBatch, query, where, getDocs, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { FIRESTORE } from '../firebase.config';
import { UserProfile } from '../models/user.model';
import { AuthService } from '../auth/auth.service';
import { DataService } from './data.service';
import { AppCacheService } from './app-cache.service';
import { LOAN_STATUS } from '../constants';
import type { ClientRelationship, AccessibleClient } from '../models/relationship.model';
import type { ClientAccount } from '../models/account.model';

@Injectable({ providedIn: 'root' })
export class ClientRelationshipService {
  private authService = inject(AuthService);
  private dataService = inject(DataService);
  private cache = inject(AppCacheService);
  private injector = inject(Injector);
  private allCommitments = this.dataService.clientCommitments;

  private async getCachedUserProfile(userId: string): Promise<UserProfile | null> {
    const fromDataService = this.dataService.getUserById(userId);
    if (fromDataService) return fromDataService;
    return await this.cache.readThrough(`user-profile:${userId}`, async () => {
      const userDoc = await getDoc(doc(FIRESTORE, 'users', userId));
      return userDoc.exists() ? { uid: userId, ...userDoc.data() } as UserProfile : null;
    }, 5 * 60 * 1000);
  }

  private normalizeRelationshipForTrustee(relationship: ClientRelationship, trusteeId: string): ClientRelationship | null {
      if (relationship.associatedUserId === trusteeId) {
        return relationship;
      }
  
      if (relationship.primaryClientId !== trusteeId) {
        return null;
      }
  
      return {
        ...relationship,
        primaryClientId: relationship.associatedUserId,
        primaryClientName: relationship.associatedUserName,
        associatedUserId: trusteeId,
        associatedUserName: relationship.primaryClientName,
      };
    }

  private normalizeRelationshipForClient(relationship: ClientRelationship, clientId: string): ClientRelationship | null {
      if (relationship.primaryClientId === clientId) {
        return relationship;
      }
  
      if (relationship.associatedUserId !== clientId) {
        return null;
      }
  
      return {
        ...relationship,
        primaryClientId: clientId,
        primaryClientName: relationship.associatedUserName,
        associatedUserId: relationship.primaryClientId,
        associatedUserName: relationship.primaryClientName,
      };
    }

  private async resolveRelationshipParties(relationship: ClientRelationship): Promise<{ clientId: string; trusteeId: string }> {
      const [primaryProfile, associatedProfile] = await Promise.all([
        this.getCachedUserProfile(relationship.primaryClientId),
        this.getCachedUserProfile(relationship.associatedUserId)
      ]);
  
      const primaryIsTrustee = primaryProfile?.subRole === 'Trustee' || primaryProfile?.subRole === 'ReadOnly';
      const associatedIsTrustee = associatedProfile?.subRole === 'Trustee' || associatedProfile?.subRole === 'ReadOnly';
  
      if (primaryIsTrustee && !associatedIsTrustee) {
        return { clientId: relationship.associatedUserId, trusteeId: relationship.primaryClientId };
      }
  
      if (!primaryIsTrustee && associatedIsTrustee) {
        return { clientId: relationship.primaryClientId, trusteeId: relationship.associatedUserId };
      }
  
      return { clientId: relationship.primaryClientId, trusteeId: relationship.associatedUserId };
    }

  private relationshipKey(primaryClientId: string, associatedUserId: string): string {
      return `${primaryClientId}::${associatedUserId}`;
    }

  private dedupeRelationships(relationships: ClientRelationship[]): ClientRelationship[] {
      const unique = new Map<string, ClientRelationship>();
      for (const relationship of relationships) {
        const key = this.relationshipKey(relationship.primaryClientId, relationship.associatedUserId);
        if (!unique.has(key)) {
          unique.set(key, relationship);
        }
      }
      return [...unique.values()];
    }

  private async getManagedRelationships(userId: string): Promise<ClientRelationship[]> {
      const [canonicalRelationships, legacyRelationships] = await Promise.all([
        this.dataService.query<ClientRelationship>('clientRelationships', [
          { field: 'associatedUserId', operator: '==', value: userId },
          { field: 'isActive', operator: '==', value: true }
        ]),
        this.dataService.query<ClientRelationship>('clientRelationships', [
          { field: 'primaryClientId', operator: '==', value: userId },
          { field: 'isActive', operator: '==', value: true }
        ])
      ]);
  
      return this.dedupeRelationships([
        ...canonicalRelationships
          .map(relationship => this.normalizeRelationshipForTrustee(relationship, userId))
          .filter((relationship): relationship is ClientRelationship => relationship !== null),
        ...legacyRelationships
          .map(relationship => this.normalizeRelationshipForTrustee(relationship, userId))
          .filter((relationship): relationship is ClientRelationship => relationship !== null),
      ]);
    }

  private async getClientRelationships(clientId: string): Promise<ClientRelationship[]> {
      const [canonicalRelationships, legacyRelationships] = await Promise.all([
        this.dataService.query<ClientRelationship>('clientRelationships', [
          { field: 'primaryClientId', operator: '==', value: clientId }
        ]),
        this.dataService.query<ClientRelationship>('clientRelationships', [
          { field: 'associatedUserId', operator: '==', value: clientId }
        ])
      ]);
  
      return this.dedupeRelationships([
        ...canonicalRelationships
          .map(relationship => this.normalizeRelationshipForClient(relationship, clientId))
          .filter((relationship): relationship is ClientRelationship => relationship !== null),
        ...legacyRelationships
          .map(relationship => this.normalizeRelationshipForClient(relationship, clientId))
          .filter((relationship): relationship is ClientRelationship => relationship !== null),
      ]);
    }

  /**
     * Check if a user has any active trustee/read-only relationships
     * This is async because it needs to query Firestore
     */
    async hasActiveRelationships(userId: string): Promise<boolean> {
      console.log('[TRUSTEE DEBUG] hasActiveRelationships() called for userId:', userId);
      try {
        console.log('[TRUSTEE DEBUG] Querying clientRelationships collection...');
        const relationships = await this.dataService.query<ClientRelationship>('clientRelationships', [
          { field: 'associatedUserId', operator: '==', value: userId },
          { field: 'isActive', operator: '==', value: true }
        ]);
        console.log('[TRUSTEE DEBUG] Query completed. Found relationships:', relationships.length);
        if (relationships.length > 0) {
          console.log('[TRUSTEE DEBUG] Relationships:', relationships);
          return true;
        }
        const legacyRelationships = await this.dataService.query<ClientRelationship>('clientRelationships', [
          { field: 'primaryClientId', operator: '==', value: userId },
          { field: 'isActive', operator: '==', value: true }
        ]);
        console.log('[TRUSTEE DEBUG] Legacy relationship query completed. Found relationships:', legacyRelationships.length);
        if (legacyRelationships.length > 0) {
          console.log('[TRUSTEE DEBUG] Legacy relationships:', legacyRelationships);
        }
        return legacyRelationships.length > 0;
      } catch (error) {
        console.error('[TRUSTEE DEBUG] Error checking relationships:', error);
        console.error('[TRUSTEE DEBUG] Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          code: (error as any)?.code,
          details: (error as any)?.details
        });
        return false;
      }
    }

  /**
     * REQUEST approval to create a client relationship (four-eyes governance)
     */
    async requestCreateClientRelationship(
      primaryClientId: string,
      primaryClientName: string,
      associatedUserId: string,
      associatedUserName: string,
      relationshipType: 'Trustee' | 'ReadOnly',
      canCommit: boolean = false
    ): Promise<string> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can request client relationships');
      }
  
      // Import ApprovalService dynamically to avoid circular dependency
      const { ApprovalService } = await import('./approval.service');
      const approvalService = this.injector.get(ApprovalService);
  
      return await approvalService.createApprovalRequest({
        type: 'ASSOCIATED_USER_CREATE',
        description: `Add ${relationshipType} access: ${associatedUserName} → ${primaryClientName}`,
        trusteeId: associatedUserId,
        trusteeName: associatedUserName,
        clientId: primaryClientId,
        clientName: primaryClientName,
        relationshipType,
        canCommit,
        metadata: {
          relationshipType,
          canCommit
        }
      });
    }

  /**
     * REQUEST approval to add a client account (four-eyes governance)
     */
    async requestAddClientAccount(
      userId: string,
      userName: string,
      accountData: import('../models/account.model').ClientAccount
    ): Promise<string> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can request client account additions');
      }
  
      // Import ApprovalService dynamically to avoid circular dependency
      const { ApprovalService } = await import('./approval.service');
      const approvalService = this.injector.get(ApprovalService);
  
      return await approvalService.createApprovalRequest({
        type: 'CLIENT_ACCOUNT_CREATE',
        description: `Add ${accountData.accountType} account for ${userName}`,
        userId,
        userName,
        accountType: accountData.accountType,
        accountNumber: accountData.id, // Account ID serves as reference
        metadata: {
          accountData
        }
      });
    }

  /**
     * Admin: Create a relationship between a primary client and an associated user (Trustee/ReadOnly)
     */
    async createClientRelationship(
      primaryClientId: string,
      associatedUserId: string,
      relationshipType: 'Trustee' | 'ReadOnly',
      allowedAccounts?: string[],
      canCommitOverride?: boolean
    ): Promise<void> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can create client relationships');
      }
  
      // Fetch client and user details
      const [primaryClientDoc, associatedUserDoc] = await Promise.all([
        this.getCachedUserProfile(primaryClientId),
        this.getCachedUserProfile(associatedUserId)
      ]);
  
      if (!primaryClientDoc || !associatedUserDoc) {
        throw new Error('Client or associated user not found');
      }
  
      const primaryClient = primaryClientDoc;
      const associatedUser = associatedUserDoc;
      const relationshipId = `${primaryClientId}_${associatedUserId}`;
      const legacyRelationshipId = `${associatedUserId}_${primaryClientId}`;
  
      const [canonicalRelationship, legacyRelationship] = await Promise.all([
        getDoc(doc(FIRESTORE, 'clientRelationships', relationshipId)),
        getDoc(doc(FIRESTORE, 'clientRelationships', legacyRelationshipId))
      ]);
  
      const existingRelationship = canonicalRelationship.exists()
        ? canonicalRelationship.data() as ClientRelationship
        : legacyRelationship.exists()
          ? legacyRelationship.data() as ClientRelationship
          : null;
  
      const canCommit = canCommitOverride ?? (relationshipType === 'Trustee');
      const permissions = {
        canCommit,
        canViewDocuments: true,
        canDownloadDocuments: true,
        canViewFinancials: true
      };
  
      const relationship: Omit<ClientRelationship, 'id'> = {
        primaryClientId,
        primaryClientName: primaryClient.displayName,
        associatedUserId,
        associatedUserName: associatedUser.displayName,
        associatedUserEmail: associatedUser.email,
        relationshipType,
        ...permissions,
        ...(allowedAccounts ?? existingRelationship?.allowedAccounts ? { allowedAccounts: allowedAccounts ?? existingRelationship?.allowedAccounts } : {}),
        createdAt: existingRelationship?.createdAt || new Date().toISOString(),
        createdBy: existingRelationship?.createdBy || admin.uid,
        notes: existingRelationship?.notes,
        isActive: true
      };
  
      const batch = writeBatch(FIRESTORE);
      batch.set(doc(FIRESTORE, 'clientRelationships', relationshipId), {
        ...relationship,
        id: relationshipId
      });
      if (legacyRelationship.exists()) {
        batch.delete(legacyRelationship.ref);
      }
  
      const userUpdates: Partial<UserProfile> = {};
  
      if (associatedUser.subRole !== relationshipType) {
        userUpdates.subRole = relationshipType;
      }
  
      const currentAssignedIds = associatedUser.assignedClientIds || [];
      if (!currentAssignedIds.includes(primaryClientId)) {
        userUpdates.assignedClientIds = [...currentAssignedIds, primaryClientId];
      }
  
      if (Object.keys(userUpdates).length > 0) {
        batch.update(doc(FIRESTORE, 'users', associatedUserId), userUpdates);
      }
  
      await batch.commit();
  
      if (Object.keys(userUpdates).length > 0) {
        this.cache.invalidate(`user-profile:${associatedUserId}`);
      }
    }

  /**
     * Admin: Update an existing client relationship (or create if missing).
     */
    async updateClientRelationship(
      primaryClientId: string,
      associatedUserId: string,
      relationshipType: 'Trustee' | 'ReadOnly',
      canCommitOverride?: boolean,
      allowedAccounts?: string[]
    ): Promise<void> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can update client relationships');
      }
  
      const [primaryClientDoc, associatedUserDoc] = await Promise.all([
        this.getCachedUserProfile(primaryClientId),
        this.getCachedUserProfile(associatedUserId)
      ]);
  
      if (!primaryClientDoc || !associatedUserDoc) {
        throw new Error('Client or associated user not found');
      }
  
      const canCommit = relationshipType === 'Trustee'
        ? (canCommitOverride ?? true)
        : false;
  
      const timestamp = new Date().toISOString();
      const canonicalId = `${primaryClientId}_${associatedUserId}`;
      const legacyId = `${associatedUserId}_${primaryClientId}`;
      const canonicalRef = doc(FIRESTORE, 'clientRelationships', canonicalId);
      const legacyRef = doc(FIRESTORE, 'clientRelationships', legacyId);
      const [canonicalSnap, legacySnap] = await Promise.all([
        getDoc(canonicalRef),
        getDoc(legacyRef),
      ]);
  
      const currentRelationship = canonicalSnap.exists()
        ? canonicalSnap.data() as ClientRelationship
        : legacySnap.exists()
          ? legacySnap.data() as ClientRelationship
          : null;
  
      const updates: Omit<ClientRelationship, 'id'> = {
        primaryClientId,
        primaryClientName: primaryClientDoc.displayName,
        associatedUserId,
        associatedUserName: associatedUserDoc.displayName,
        associatedUserEmail: associatedUserDoc.email,
        relationshipType,
        canCommit,
        canViewDocuments: true,
        canDownloadDocuments: true,
        canViewFinancials: true,
        isActive: true,
        ...(allowedAccounts ?? currentRelationship?.allowedAccounts
          ? { allowedAccounts: allowedAccounts ?? currentRelationship?.allowedAccounts }
          : {}),
        createdBy: currentRelationship?.createdBy || admin.uid,
        createdAt: currentRelationship?.createdAt || timestamp,
        notes: currentRelationship?.notes,
        updatedBy: admin.uid,
        updatedAt: timestamp,
      };
      const batch = writeBatch(FIRESTORE);
      batch.set(canonicalRef, {
        ...updates,
        id: canonicalId,
      } as ClientRelationship);
      if (legacySnap.exists()) {
        batch.delete(legacyRef);
      }
      await batch.commit();
  
      const userUpdates: Partial<UserProfile> = {};
      if (associatedUserDoc.subRole !== relationshipType) {
        userUpdates.subRole = relationshipType;
      }
  
      const currentAssignedIds = associatedUserDoc.assignedClientIds || [];
      if (!currentAssignedIds.includes(primaryClientId)) {
        userUpdates.assignedClientIds = [...currentAssignedIds, primaryClientId];
      }
  
      if (Object.keys(userUpdates).length > 0) {
        await updateDoc(doc(FIRESTORE, 'users', associatedUserId), userUpdates);
        this.cache.invalidate(`user-profile:${associatedUserId}`);
      }
    }

  /**
     * Admin: Deactivate a client relationship
     */
    async deactivateClientRelationship(relationshipId: string): Promise<void> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can deactivate relationships');
      }
  
      const relationshipDoc = await getDoc(doc(FIRESTORE, 'clientRelationships', relationshipId));
      if (!relationshipDoc.exists()) {
        throw new Error('Relationship not found');
      }
  
      const relationship = relationshipDoc.data() as ClientRelationship;
      const { clientId, trusteeId } = await this.resolveRelationshipParties(relationship);
      const timestamp = new Date().toISOString();
      const canonicalRef = doc(FIRESTORE, 'clientRelationships', `${clientId}_${trusteeId}`);
      const legacyRef = doc(FIRESTORE, 'clientRelationships', `${trusteeId}_${clientId}`);
      const [canonicalSnap, legacySnap] = await Promise.all([
        getDoc(canonicalRef),
        getDoc(legacyRef),
      ]);
  
      const batch = writeBatch(FIRESTORE);
      const updatePayload = {
        isActive: false,
        updatedBy: admin.uid,
        updatedAt: timestamp,
      };
  
      if (canonicalSnap.exists()) {
        batch.update(canonicalRef, updatePayload);
      }
  
      if (legacySnap.exists()) {
        batch.update(legacyRef, updatePayload);
      }
  
      await batch.commit();
    }

  /**
     * Admin: Reactivate a client relationship
     */
    async reactivateClientRelationship(relationshipId: string): Promise<void> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can reactivate relationships');
      }
  
      const relationshipDoc = await getDoc(doc(FIRESTORE, 'clientRelationships', relationshipId));
      if (!relationshipDoc.exists()) {
        throw new Error('Relationship not found');
      }
  
      const relationship = relationshipDoc.data() as ClientRelationship;
      const { clientId, trusteeId } = await this.resolveRelationshipParties(relationship);
      const timestamp = new Date().toISOString();
      const canonicalRef = doc(FIRESTORE, 'clientRelationships', `${clientId}_${trusteeId}`);
      const legacyRef = doc(FIRESTORE, 'clientRelationships', `${trusteeId}_${clientId}`);
      const [canonicalSnap, legacySnap] = await Promise.all([
        getDoc(canonicalRef),
        getDoc(legacyRef),
      ]);
  
      const batch = writeBatch(FIRESTORE);
      const updatePayload = {
        isActive: true,
        updatedBy: admin.uid,
        updatedAt: timestamp,
      };
  
      if (canonicalSnap.exists()) {
        batch.update(canonicalRef, updatePayload);
      }
  
      if (legacySnap.exists()) {
        batch.update(legacyRef, updatePayload);
      }
  
      await batch.commit();
    }

  /**
     * Admin: Completely delete a client relationship
     */
    async deleteClientRelationship(relationshipId: string): Promise<void> {
      const admin = this.authService.userProfile();
      if (!admin || admin.role !== 'Admin') {
        throw new Error('Only admins can delete relationships');
      }
  
      // First, get the relationship to extract the user ID and client ID
      const relationshipDoc = await getDoc(doc(FIRESTORE, 'clientRelationships', relationshipId));
      if (!relationshipDoc.exists()) {
        throw new Error('Relationship not found');
      }
  
      const relationship = relationshipDoc.data() as ClientRelationship;
      const { clientId, trusteeId } = await this.resolveRelationshipParties(relationship);
  
      // Remove the client ID from the trustee's assignedClientIds
      const userData = await this.getCachedUserProfile(trusteeId);
      if (userData) {
        const currentAssignedIds = userData.assignedClientIds || [];
        const updatedAssignedIds = currentAssignedIds.filter(id => id !== clientId);
  
        await updateDoc(doc(FIRESTORE, 'users', trusteeId), {
          assignedClientIds: updatedAssignedIds
        });
        this.cache.invalidate(`user-profile:${trusteeId}`);
      }
  
      // Delete both canonical and legacy document IDs so old inverted records do not linger.
      const batch = writeBatch(FIRESTORE);
      batch.delete(doc(FIRESTORE, 'clientRelationships', `${clientId}_${trusteeId}`));
      batch.delete(doc(FIRESTORE, 'clientRelationships', `${trusteeId}_${clientId}`));
      await batch.commit();
    }

  /**
     * Admin: Get all relationships for a specific client
     */
    async getRelationshipsForClient(clientId: string): Promise<ClientRelationship[]> {
      try {
        return await this.getClientRelationships(clientId);
      } catch (error) {
        console.error('Error fetching relationships for client:', error);
        return [];
      }
    }

  /**
     * User: Get all clients this user can access with relationship details
     */
    async getAccessibleClients(userId: string): Promise<AccessibleClient[]> {
      console.log('[TRUSTEE DEBUG] ========================================');
      console.log('[TRUSTEE DEBUG] getAccessibleClients() called');
      console.log('[TRUSTEE DEBUG] userId:', userId);
      console.log('[TRUSTEE DEBUG] ========================================');
  
      try {
        console.log('[TRUSTEE DEBUG] Step 1: Querying clientRelationships...');
        console.log('[TRUSTEE DEBUG] Query filters:', {
          associatedUserId: userId,
          isActive: true
        });
  
        const normalizedRelationships = await this.getManagedRelationships(userId);
  
        console.log('[TRUSTEE DEBUG] Step 2: Query completed');
        console.log('[TRUSTEE DEBUG] Found', normalizedRelationships.length, 'relationship(s)');
  
        if (normalizedRelationships.length === 0) {
          console.log('[TRUSTEE DEBUG] No relationships found - returning empty array');
          return [];
        }
  
        console.log('[TRUSTEE DEBUG] Relationships found:', normalizedRelationships.map(r => ({
          id: r.id,
          clientId: r.primaryClientId,
          clientName: r.primaryClientName,
          relationshipType: r.relationshipType,
          isActive: r.isActive
        })));
  
        const result: AccessibleClient[] = [];
  
        console.log('[TRUSTEE DEBUG] Step 3: Processing relationships...');
        for (const relationship of normalizedRelationships) {
          console.log('[TRUSTEE DEBUG] Processing relationship for client:', relationship.primaryClientId);
  
          try {
            const clientDoc = await getDoc(doc(FIRESTORE, 'users', relationship.primaryClientId));
  
            if (!clientDoc.exists()) {
              console.warn('[TRUSTEE DEBUG] Client document not found for ID:', relationship.primaryClientId);
              continue;
            }
  
            const client = { uid: relationship.primaryClientId, ...clientDoc.data() } as UserProfile;
            console.log('[TRUSTEE DEBUG] Client found:', client.displayName);
  
            // Get stats for this client
            console.log('[TRUSTEE DEBUG] Fetching commitments for client...');
            const clientCommitments = this.allCommitments().filter(c => c.userId === client.uid);
            console.log('[TRUSTEE DEBUG] Found', clientCommitments.length, 'commitments');
  
            const totalCommitted = clientCommitments.reduce((sum, c) => sum + c.committedAmount, 0);
            const deals = this.dataService.allDeals();
            console.log('[TRUSTEE DEBUG] Retrieved all deals:', deals.length);
            const dealIds = new Set(clientCommitments.map(c => c.loanDealId));
            console.log('[TRUSTEE DEBUG] Unique deal IDs from commitments:', dealIds.size);
  
            let activeDealsCount = 0;
            let fundedDealsCount = 0;
  
            for (const dealId of dealIds) {
              const deal = deals.find(d => d.loanDeal.id === dealId);
              if (deal) {
                console.log(`[TRUSTEE DEBUG] Found deal ${dealId} with status: ${deal.loanDeal.status}`);
                if (deal.loanDeal.status === LOAN_STATUS.ACTIVE) activeDealsCount++;
                if (deal.loanDeal.status === LOAN_STATUS.FUNDED || deal.loanDeal.status === LOAN_STATUS.EXTENDED) fundedDealsCount++;
              } else {
                console.warn(`[TRUSTEE DEBUG] Deal ${dealId} not found in allDeals()`);
              }
            }
  
            console.log('[TRUSTEE DEBUG] Client stats:', {
              totalCommitted,
              activeDealsCount,
              fundedDealsCount
            });
  
            result.push({
              client,
              relationship,
              stats: {
                totalCommitted,
                activeDealsCount,
                fundedDealsCount
              }
            });
  
            console.log('[TRUSTEE DEBUG] Added client to result array');
          } catch (clientError) {
            console.error('[TRUSTEE DEBUG] Error processing client:', relationship.primaryClientId, clientError);
          }
        }
  
        console.log('[TRUSTEE DEBUG] ========================================');
        console.log('[TRUSTEE DEBUG] Final result: Returning', result.length, 'accessible client(s)');
        console.log('[TRUSTEE DEBUG] ========================================');
        return result;
      } catch (error) {
        console.error('[TRUSTEE DEBUG] ========================================');
        console.error('[TRUSTEE DEBUG] ERROR in getAccessibleClients');
        console.error('[TRUSTEE DEBUG] Error:', error);
        console.error('[TRUSTEE DEBUG] Error message:', error instanceof Error ? error.message : 'Unknown');
        console.error('[TRUSTEE DEBUG] Error code:', (error as any)?.code);
        console.error('[TRUSTEE DEBUG] Error stack:', error instanceof Error ? error.stack : 'N/A');
        console.error('[TRUSTEE DEBUG] ========================================');
        return [];
      }
    }

  /**
     * Get clients managed by a Trustee or accessible by a ReadOnly user.
     * Queries the clientRelationships collection to find active relationships.
     */
    async getManagedClients(userId: string): Promise<UserProfile[]> {
      try {
        const normalized = await this.getManagedRelationships(userId);
        if (normalized.length === 0) {
          return [];
        }
  
        const clientIds = normalized.map(r => r.primaryClientId);
        const clients = await Promise.all(clientIds.map(id => this.getCachedUserProfile(id)));
  
        return clients.filter((c): c is UserProfile => c !== null);
      } catch (error) {
        console.error('Error fetching managed clients:', error);
        return [];
      }
    }

  /**
     * Efficiently check if a user manages any clients (is a Trustee or ReadOnly).
     * Uses limit(1) to avoid fetching all relationships.
     */
    async hasManagedClients(userId: string): Promise<boolean> {
      try {
        return (await this.getManagedRelationships(userId)).length > 0;
      } catch (error) {
        console.error('Error checking managed clients:', error);
        return false;
      }
    }

}
